import BlueBtn from "../button/PrimaryBtn";
import ThankyouImg from "../../assets/images/cms/thankyou.svg";
import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { reportQuestionAPI } from "../../api/user";
import { useMutation, useQuery } from "@tanstack/react-query";
import useToast from "../../hooks/useToast";
import { getReportQuestionReasonsAPI } from "../../api/user";
import { useTranslation } from "react-i18next";
import PrimaryBtn from "../button/PrimaryBtn";

const ReportQuestionModal = ({ isOpen, onClose, questionData, quistionIdToReport }) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const OTHER_REASON_KEY = "__other__";
  const { t } = useTranslation();
  const { data: reportQuestionReasons } = useQuery({
    queryKey: ["reportQuestionReasons"],
    queryFn: getReportQuestionReasonsAPI,
  });

  const reasons = reportQuestionReasons?.data || [];

  const { values, errors, touched, handleBlur, handleSubmit, isValid, setFieldValue } = useFormik({
    initialValues: {
      question_id: quistionIdToReport || "",
      question_type: questionData?.report_question_type || "game-question",
      reason: "",
      other_reason: "",
    },
    enableReinitialize: true, // Re-initialize when questionData changes
    validationSchema: Yup.object().shape({
      question_id: Yup.string().required(t("reportQuestionModal.questionIdRequired")),
      question_type: Yup.string().required(t("reportQuestionModal.questionTypeRequired")),
      reason: Yup.string().required(t("reportQuestionModal.reasonRequired")),
      other_reason: Yup.string().when("reason", {
        is: OTHER_REASON_KEY,
        then: (schema) =>
          schema
            .trim()
            .required(t("reportQuestionModal.reasonRequired"))
            .max(200, t("validation.maxCharacters", { max: 200 })),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: (values) => {
      const reasonToSubmit = values.reason === OTHER_REASON_KEY ? values.other_reason.trim() : values.reason;
      const payload = {
        question_id: values.question_id,
        question_type: values.question_type,
        reason: reasonToSubmit,
      };
      mutate(payload);
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: reportQuestionAPI,
    onSuccess: (data) => {
      setIsSuccess(true);
      useToast(data.message, "success");
      onClose();
    },
    onError: (error: Error) => {
      useToast(error.message, "error");
    },
  });

  const handleReportQuestionSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit();
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center lg:p-[1%] p-4 z-50">
      <div className="modal bg-white-500 2xl:rounded-[2.083vw] lg:rounded-[40px] md:rounded-4xl rounded-3xl 2xl:p-[2vw] xl:p-10 md:p-7 p-4 2xl:max-w-[31vw] max-w-[620px] w-full relative animate-[slideUp_0.3s_ease-out] lg:border-[0.2vw] border-3 border-primary auth-page overflow-auto lg:max-h-[calc(100dvh-5%)] max-h-[calc(100dvh-20px)] hide-scroll">
        {!isSuccess && (
          <div>
            <div className="flex justify-between">
              <h4 className="2xl:text-[1.875vw]/[1.9vw] xl:text-4xl/6 text-2xl/6 font-semibold text-primary-orange mb-0">
                {t("reportQuestionModal.title")}
              </h4>
              <button
                onClick={() => {
                  setIsSuccess(false);
                  onClose();
                }}
                className="2xl:mt-[0.20vw] mt-1 lg:w-[1.2vw] lg:h-[1.2vw] md:w-5 w-4 h-4 flex items-center justify-center cursor-pointer ms-auto hover:scale-110 transition-transform duration-300"
              >
                <svg
                  className="w-full h-full"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.9474 12.1123C15.8856 12.0504 15.8856 11.9496 15.9474 11.8878L23.5029 4.3286C24.149 3.68231 24.149 2.63063 23.5029 1.98429L22.0051 0.48573C21.692 0.172468 21.2758 0 20.833 0C20.3902 0 19.9739 0.172468 19.6609 0.485683L12.1054 8.04493C12.0672 8.08316 12.0235 8.09122 11.9937 8.09122C11.9639 8.09122 11.9203 8.08321 11.8821 8.04493L4.32658 0.485683C4.01351 0.172468 3.59726 0 3.15445 0C2.71164 0 2.29544 0.172468 1.98236 0.485683L0.484547 1.98429C-0.161516 2.63063 -0.161516 3.68231 0.484547 4.3286L8.04001 11.8878C8.10185 11.9496 8.10185 12.0504 8.04001 12.1123L0.484547 19.6714C-0.161469 20.3178 -0.161469 21.3694 0.484547 22.0157L1.98236 23.5143C2.29544 23.8275 2.71168 24 3.15445 24C3.59721 24 4.01351 23.8275 4.32658 23.5143L11.882 15.9551C11.9203 15.9169 11.9638 15.9089 11.9937 15.9089C12.0235 15.9089 12.0671 15.9169 12.1053 15.9552L19.6608 23.5143C19.9739 23.8276 20.3901 24 20.8329 24C21.2758 24 21.692 23.8276 22.005 23.5143L23.5028 22.0158C24.1489 21.3695 24.1489 20.3178 23.5028 19.6715L15.9474 12.1123Z"
                    fill="#333938"
                  />
                </svg>
              </button>
            </div>
            <hr className="border-0 2xl:h-[0.0265vw] h-[0.5px] w-full 2xl:my-[1.67vw] xl:my-8 md:my-5 my-4 landscape:max-lg:mt-4 bg-[linear-gradient(90deg,rgba(11,51,105,0)_0%,#7364FF_50%,rgba(11,51,105,0)_100%)]" />
            <p className="2xl:text-[1.041vw]/[1.15vw] md:text-xl/tight text-base/tight landscape:max-lg:text-base/tight text-black-800 text-center font-bold">
              {t("reportQuestionModal.description")}
            </p>
            <form action="" className="2xl:mt-[1.67vw] xl:mt-8 md:mt-5 mt-4 landscape:max-lg:mt-4" onSubmit={handleReportQuestionSubmit}>
              <div className="2xl:max-h-[15vw] max-h-[280px] overflow-y-auto scroll-width-small 2xl:space-y-[0.5vw] space-y-2 px-4">
                {reasons.map((item: { id: string; reason: string }) => (
                  <label
                    key={item.id}
                    htmlFor={`reason-${item.id}`}
                    className={`w-full skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] cursor-pointer transition-all ${values.reason === item.reason
                      ? "bg-primary-orange text-white"
                      : "bg-[#E7E6E6] text-black-300 hover:opacity-80"
                      }`}
                  >
                    <input
                      type="radio"
                      id={`reason-${item.id}`}
                      name="reason"
                      value={item.reason}
                      checked={values.reason === item.reason}
                      onChange={() => {
                        setFieldValue("reason", item.reason);
                      }}
                      onBlur={handleBlur}
                      className="hidden"
                    />
                    <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 flex items-center 2xl:gap-[0.6vw] gap-3 w-full">
                      <span
                        className={`2xl:w-[1.1vw] 2xl:h-[1.1vw] w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${values.reason === item.reason ? "border-white" : "border-black-400"
                          }`}
                      >
                        {values.reason === item.reason && (
                          <span className="2xl:w-[0.5vw] 2xl:h-[0.5vw] w-2.5 h-2.5 rounded-full bg-white"></span>
                        )}
                      </span>
                      <span className="2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold">
                        {item.reason}
                      </span>
                    </span>
                  </label>
                ))}
                <label
                  htmlFor="reason-other"
                  className={`w-full skew-x-[-10deg] 2xl:border-[0.104vw] border-[1.5px] border-black-300 flex items-center 2xl:py-[0.5vw] xl:py-1.5 py-1 2xl:px-[1.042vw] lg:px-4 md:px-3 px-2.5 2xl:rounded-[0.26vw] rounded-[5px] 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706] cursor-pointer transition-all ${values.reason === OTHER_REASON_KEY
                    ? "bg-primary-orange text-white"
                    : "bg-[#E7E6E6] text-black-300 hover:opacity-80"
                    }`}
                >
                  <input
                    type="radio"
                    id="reason-other"
                    name="reason"
                    value={OTHER_REASON_KEY}
                    checked={values.reason === OTHER_REASON_KEY}
                    onChange={() => setFieldValue("reason", OTHER_REASON_KEY)}
                    onBlur={handleBlur}
                    className="hidden"
                  />
                  <span className="skew-x-[10deg] 2xl:mt-[0.208vw] mt-1 flex items-center 2xl:gap-[0.6vw] gap-3 w-full">
                    <span
                      className={`2xl:w-[1.1vw] 2xl:h-[1.1vw] w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${values.reason === OTHER_REASON_KEY ? "border-white" : "border-black-400"
                        }`}
                    >
                      {values.reason === OTHER_REASON_KEY && (
                        <span className="2xl:w-[0.5vw] 2xl:h-[0.5vw] w-2.5 h-2.5 rounded-full bg-white"></span>
                      )}
                    </span>
                    <span className="2xl:text-[0.9375vw]/[1.04vw] xl:text-base/tight text-sm/tight font-bold">
                      {t("reportQuestionModal.other") || "Other"}
                    </span>
                  </span>
                </label>
              </div>
              {values.reason === OTHER_REASON_KEY && (
                <div className="2xl:mt-[0.7vw] mt-3 px-4">
                  <div className="relative 2xl:py-[0.6vw] py-2 lg:px-[5%] px-4 flex 2xl:rounded-[0.26vw] rounded-[5px] skew-x-[-6deg] 2xl:border-[0.104vw] border-[1.5px] transition-all border-black-300 2xl:shadow-[0px_0.156vw_0px_#110706] shadow-[0px_2px_0px_#110706]">
                    <textarea
                      name="other_reason"
                      value={values.other_reason}
                      onChange={(e) => setFieldValue("other_reason", e.target.value)}
                      onBlur={handleBlur}
                      maxLength={200}
                      placeholder={t("reportQuestionModal.writeYourReason") || "Write your reason..."}
                      className="2xl:mt-[0.208vw] mt-1 rtl:dir-rtl rtl:text-right w-full lg:text-[1.1vw]/none text-base/none font-bold skew-x-[10deg] text-black-300 placeholder:text-black-400 focus:outline-none transition-all disabled:opacity-50 bg-transparent 2xl:h-[5.72vw] md:h-[110px] h-[80px] landscape:max-lg:h-[80px] resize-none"
                    />
                  </div>
                  <div className="flex justify-end mt-1">
                    <span className="text-black-500 text-xs font-semibold">{values.other_reason.length}/200</span>
                  </div>
                </div>
              )}
              {touched.reason && errors.reason && (
                <p className="text-red-500 text-sm 2xl:mt-[0.5vw] mt-2">{errors.reason}</p>
              )}
              {values.reason === OTHER_REASON_KEY && touched.other_reason && errors.other_reason && (
                <p className="text-red-500 text-sm 2xl:mt-[0.5vw] mt-2">{errors.other_reason}</p>
              )}
              {errors.question_id && <p className="text-red-500 text-sm mt-1">{t("reportQuestionModal.questionDataMissing")}</p>}
              <PrimaryBtn
                type="submit"
                className="min-w-full 2xl:mt-[1.67vw] xl:mt-8 md:mt-5 mt-4 landscape:max-lg:mt-4 lg:mb-0 md:mb-0 mb-0"
                disabled={isPending || !!!values.reason}
                loading={isPending}
              >
                {t("reportQuestionModal.submit")}
              </PrimaryBtn>
            </form>
          </div>
        )}
        {/* After send report */}
        {isSuccess && (
          <div className="text-center">
            <img
              src={ThankyouImg}
              alt=""
              className="w-auto 2xl:h-[7.29vw] md:h-36 h-20 landscape:max-lg:h-20 mx-auto 2xl:mb-[1.50vw] md:mb-[30px] mb-3 landscape:max-lg:mb-3 rtl:-scale-x-100"
            />
            <h4 className="2xl:text-[1.5625vw]/[1.7vw] xl:text-3xl/6 text-2xl/6 font-bold text-primary-orange 2xl:mb-[0.42vw] md:mb-2 mb-1 landscape:max-lg:mb-1">
              {t("reportQuestionModal.thankYou")}
            </h4>
            <p className="2xl:text-[1.041vw]/[1.15vw] md:text-xl/tight text-base/tight landscape:max-lg:text-base/tight text-black-800 text-center font-bold">
              {t("reportQuestionModal.yourReportHasBeenSuccessfullySubmittedToOurAdminTeamWeWillReviewAndUpdateTheQuestionAccordingly")}
            </p>
            <PrimaryBtn
              onClick={() => {
                setIsSuccess(false);
                onClose();
              }}
              className="min-w-full 2xl:mt-[1.67vw] xl:mt-8 md:mt-5 mt-4 landscape:max-lg:mt-4 lg:mb-0 md:mb-0 mb-0"
            >
              {t("reportQuestionModal.gotIt")}
            </PrimaryBtn>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportQuestionModal;
